import { useState } from 'react';
import clsx from 'clsx';
import { ArrowDown, ArrowUp, BarChart3, ListChecks, Plus, Trash2, X } from 'lucide-react';
import { ApiError, POLL_QUESTION_LABELS, type PollInput, type PollQuestionKind } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { messageStore } from '@/lib/messageStore';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Button, Field, Input, Label, Modal, Select, Textarea, Toggle } from '@/components/ui';
import { pollKey } from './PollCard';

type Q = PollInput['questions'][number] & { key: string; optionsText?: string };

const newQuestion = (kind: PollQuestionKind = 'single'): Q => ({
  key: crypto.randomUUID(), kind, prompt: '', required: true, options: [], optionsText: kind === 'single' || kind === 'multi' ? '' : undefined,
  scale_min: 1, scale_max: 5, scale_labels: null,
});

export interface PollDraft { kind: 'poll' | 'survey'; question?: string; options?: string[] }

/** Quick poll (one question) or multi-question survey. */
export function PollCreateModal({ conversationId, where, initial, onClose }: { conversationId: string; where: string; initial: PollDraft; onClose: () => void }) {
  const toast = useUi(s => s.toast);
  const [kind, setKind] = useState<'poll' | 'survey'>(initial.kind);
  const [title, setTitle] = useState(initial.question ?? '');
  const [description, setDescription] = useState('');
  const [options, setOptions] = useState<string[]>(() => {
    const o = initial.options?.length ? [...initial.options] : ['', ''];
    while (o.length < 2) o.push('');
    return o;
  });
  const [multiple, setMultiple] = useState(false);
  const [questions, setQuestions] = useState<Q[]>(() => [newQuestion('rating'), newQuestion('text')].map((q, i) => (i === 1 ? { ...q, required: false } : q)));
  const [anonymous, setAnonymous] = useState(false);
  const [resultsAfter, setResultsAfter] = useState(false);
  const [allowChange, setAllowChange] = useState(true);
  const [closes, setCloses] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const patchQ = (key: string, p: Partial<Q>) => setQuestions(qs => qs.map(q => (q.key === key ? { ...q, ...p } : q)));
  const moveQ = (i: number, d: number) => setQuestions(qs => { const n = [...qs]; const [x] = n.splice(i, 1); n.splice(i + d, 0, x); return n; });

  const submit = async () => {
    const body: PollInput & { client_message_id: string } = {
      client_message_id: crypto.randomUUID(), kind, title, description: kind === 'survey' ? description || null : null,
      anonymous, results_visibility: resultsAfter ? 'after_close' : 'live', allow_change: allowChange,
      closes_at: closes ? new Date(closes).toISOString() : null,
      questions: kind === 'poll'
        ? [{ kind: multiple ? 'multi' : 'single', options: options.map(o => o.trim()).filter(Boolean) }]
        : questions.map(q => ({
          kind: q.kind, prompt: q.prompt, required: q.required, scale_min: q.scale_min, scale_max: q.scale_max, scale_labels: q.scale_labels,
          options: (q.optionsText ?? '').split('\n').map(s => s.trim()).filter(Boolean),
        })),
    };
    setBusy(true); setErrors({});
    try {
      const r = await api.createPoll(conversationId, body);
      queryClient.setQueryData(pollKey(r.poll.id), r.poll);
      messageStore.ingest(conversationId, [r.message]);
      onClose();
    } catch (e) {
      const errs = e instanceof ApiError && e.body && typeof e.body === 'object' && 'errors' in e.body ? (e.body as { errors: Record<string, string> }).errors : {};
      setErrors(errs ?? {});
      toast(errorMessage(e), 'error');
    } finally { setBusy(false); }
  };

  const err = (k: string) => errors[k] && <div className="mt-1 text-xs text-rose-600">{errors[k]}</div>;
  const minClose = new Date(Date.now() + 5 * 60_000);
  const localMin = new Date(minClose.getTime() - minClose.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);

  return (
    <Modal open wide onClose={onClose} title={<span className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-accent-fg" />New {kind === 'poll' ? 'poll' : 'survey'} in {where}</span>}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void submit()}>Post {kind}</Button></>}>
      <div className="mb-4 inline-flex rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800" role="tablist">
        {(['poll', 'survey'] as const).map(k => (
          <button key={k} role="tab" aria-selected={kind === k} onClick={() => setKind(k)}
            className={clsx('flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium', kind === k ? 'bg-white shadow-sm dark:bg-slate-700' : 'text-slate-500')}>
            {k === 'poll' ? <><BarChart3 className="h-4 w-4" />Quick poll</> : <><ListChecks className="h-4 w-4" />Survey</>}
          </button>
        ))}
      </div>
      <div className="grid gap-5 md:grid-cols-[1fr_15rem]">
        <div className="space-y-3">
          <Field label={kind === 'poll' ? 'Question' : 'Survey title'}>
            <Input autoFocus aria-label={kind === 'poll' ? 'Question' : 'Survey title'} value={title} onChange={e => setTitle(e.target.value)} maxLength={200} placeholder={kind === 'poll' ? 'e.g. Which day suits the stocktake?' : 'e.g. Warehouse safety check-in'} />
            {err('title')}
          </Field>
          {kind === 'poll' ? (
            <div>
              <Label>Options</Label>
              <div className="space-y-1.5">
                {options.map((o, i) => (
                  <div key={i} className="flex gap-1.5">
                    <Input value={o} aria-label={`Option ${i + 1}`} placeholder={`Option ${i + 1}`} maxLength={120}
                      onChange={e => setOptions(os => os.map((x, j) => (j === i ? e.target.value : x)))}
                      onKeyDown={e => { if (e.key === 'Enter' && i === options.length - 1 && o.trim() && options.length < 20) setOptions(os => [...os, '']); }} />
                    {options.length > 2 && <Button variant="ghost" size="icon" title="Remove option" onClick={() => setOptions(os => os.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>}
                  </div>
                ))}
              </div>
              {err('questions.0.options')}
              {options.length < 20 && <Button className="mt-1.5" size="sm" variant="ghost" onClick={() => setOptions(os => [...os, ''])}><Plus className="h-4 w-4" />Add option</Button>}
              <div className="mt-2"><Toggle checked={multiple} onChange={setMultiple} label="Allow several choices" /></div>
            </div>
          ) : (
            <>
              <Field label="Description (optional)"><Textarea rows={2} aria-label="Description" value={description} onChange={e => setDescription(e.target.value)} maxLength={1000} /></Field>
              {err('questions')}
              {questions.map((q, i) => (
                <div key={q.key} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700" data-question={i + 1}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500">{i + 1}</span>
                    <Input value={q.prompt} aria-label={`Question ${i + 1}`} placeholder="Question" maxLength={300} onChange={e => patchQ(q.key, { prompt: e.target.value })} />
                    <Select value={q.kind} aria-label={`Question ${i + 1} type`} className="w-40 shrink-0"
                      onChange={e => { const k = e.target.value as PollQuestionKind; patchQ(q.key, { kind: k, optionsText: k === 'single' || k === 'multi' ? q.optionsText ?? '' : q.optionsText }); }}>
                      {(Object.keys(POLL_QUESTION_LABELS) as PollQuestionKind[]).map(k => <option key={k} value={k}>{POLL_QUESTION_LABELS[k]}</option>)}
                    </Select>
                    <Button variant="ghost" size="icon" title="Move up" disabled={i === 0} onClick={() => moveQ(i, -1)}><ArrowUp className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" title="Move down" disabled={i === questions.length - 1} onClick={() => moveQ(i, 1)}><ArrowDown className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" title="Delete question" disabled={questions.length === 1} onClick={() => setQuestions(qs => qs.filter(x => x.key !== q.key))}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  {err(`questions.${i}.prompt`)}
                  <div className="mt-2 pl-5">
                    {(q.kind === 'single' || q.kind === 'multi') && (
                      <Textarea rows={3} value={q.optionsText ?? ''} aria-label={`Question ${i + 1} options`} placeholder="Options, one per line" onChange={e => patchQ(q.key, { optionsText: e.target.value })} />
                    )}
                    {q.kind === 'rating' && (
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <Select value={q.scale_min} aria-label="Scale from" className="w-20" onChange={e => patchQ(q.key, { scale_min: Number(e.target.value) })}>{[0, 1].map(n => <option key={n}>{n}</option>)}</Select>
                        to
                        <Select value={q.scale_max} aria-label="Scale to" className="w-20" onChange={e => patchQ(q.key, { scale_max: Number(e.target.value) })}>{[3, 4, 5, 7, 10].map(n => <option key={n}>{n}</option>)}</Select>
                        <Input className="w-36" placeholder="Low label" value={q.scale_labels?.[0] ?? ''} onChange={e => patchQ(q.key, { scale_labels: [e.target.value, q.scale_labels?.[1] ?? ''] })} />
                        <Input className="w-36" placeholder="High label" value={q.scale_labels?.[1] ?? ''} onChange={e => patchQ(q.key, { scale_labels: [q.scale_labels?.[0] ?? '', e.target.value] })} />
                      </div>
                    )}
                    {q.kind === 'text' && <div className="text-xs text-slate-500">People type a short answer.</div>}
                    {err(`questions.${i}.options`)}{err(`questions.${i}.scale`)}
                    <div className="mt-2"><Toggle checked={q.required !== false} onChange={v => patchQ(q.key, { required: v })} label="Required" /></div>
                  </div>
                </div>
              ))}
              {questions.length < 20 && (
                <div className="flex flex-wrap gap-1.5">
                  {(Object.keys(POLL_QUESTION_LABELS) as PollQuestionKind[]).map(k => (
                    <Button key={k} size="sm" variant="secondary" onClick={() => setQuestions(qs => [...qs, newQuestion(k)])}><Plus className="h-3.5 w-3.5" />{POLL_QUESTION_LABELS[k]}</Button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <aside className="space-y-3 rounded-lg bg-slate-50 p-3 text-sm dark:bg-slate-800/50">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Settings</div>
          <div>
            <Toggle checked={anonymous} onChange={setAnonymous} label="Anonymous" />
            <p className="mt-0.5 text-xs text-slate-500">{anonymous ? 'Nobody, including you, sees who answered what.' : 'Names are shown next to answers.'}</p>
          </div>
          <Field label="Results">
            <Select value={resultsAfter ? 'after' : 'live'} onChange={e => setResultsAfter(e.target.value === 'after')} aria-label="Results visibility">
              <option value="live">Live</option>
              <option value="after">Only after it closes</option>
            </Select>
            <p className="mt-0.5 text-xs text-slate-500">{resultsAfter ? 'You can see results while it is open; everyone else sees them when it closes.' : 'Members see results as answers come in.'}</p>
          </Field>
          <Toggle checked={allowChange} onChange={setAllowChange} label="People can change their answer" />
          <Field label="Close date (optional)">
            <Input type="datetime-local" aria-label="Close date" value={closes} min={localMin} onChange={e => setCloses(e.target.value)} />
            {err('closes_at')}
            {closes && <p className="mt-0.5 text-xs text-slate-500">People who haven't answered get a reminder a day before (if it's open that long).</p>}
          </Field>
        </aside>
      </div>
    </Modal>
  );
}
